/**
 * Toast primitive — wraps sonner for design.md-aware toast notifications.
 *
 * J-X11: Toast notifications + success confirmation patterns.
 * J-X24: responsive position — bottom-center on mobile (<640px) to avoid
 *        bottom-bar overlap; top-right on desktop.
 *
 * Exports:
 * - `Toaster` — mount at app root (responsive position, stacked, design.md tokens).
 * - re-exports `toast` from sonner for direct usage.
 *
 * Cross-cutting invariants:
 *   - Inv 12: no console.log/error/warn.
 *   - Inv 13: no `any` / `as any`.
 *   - Inv 15: no raw <button> / <a> — sonner's built-in close/action buttons
 *     are styled via toastOptions, not replaced with raw HTML.
 */
import { useEffect, useState } from 'react';
import { Toaster as SonnerToaster } from 'sonner';
import type { ReactElement } from 'react';

export { toast } from 'sonner';

/**
 * Mobile breakpoint — design.md token sm=640px. Matches shell.css mobile
 * media queries (<640px) so toast placement aligns with the bottom chrome.
 */
const MOBILE_QUERY = '(max-width: 639px)';

/**
 * Reactive viewport-is-mobile hook. SSR-safe (defaults false when matchMedia
 * is unavailable) and re-evaluates on viewport change.
 */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(MOBILE_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = (e: MediaQueryListEvent): void => {
      setIsMobile(e.matches);
    };
    setIsMobile(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isMobile;
}

/**
 * BIMAssess Toaster — mounted at app root in main.tsx.
 *
 * Configures sonner with design.md-aware tokens:
 * - Position: top-right on desktop, bottom-center on mobile (<640px) per J-X24
 * - Rich colors (uses sonner's built-in theming)
 * - Auto-dismiss: 5s for info/success, 8s for error
 * - Max visible: 5 (prevents stacking indefinitely per J-X11 failure mode 4)
 * - Gap: 24px (design.md spacing-6)
 * - Design.md class overrides for toast container
 */
export function Toaster(): ReactElement {
  const isMobile = useIsMobile();
  return (
    <SonnerToaster
      position={isMobile ? 'bottom-center' : 'top-right'}
      richColors
      duration={5000}
      gap={24}
      visibleToasts={5}
      {...(isMobile
        ? {
            // Lift the bottom-center stack above sticky bottom chrome (cookie
            // banner / offline banner) on mobile. design.md spacing-6 = 24px.
            mobileOffset: { bottom: 88, left: 16, right: 16 },
          }
        : {})}
      toastOptions={{
        classNames: {
          toast:
            'font-sans text-small rounded-md border border-line bg-card text-ink shadow-[0_1px_0_rgba(0,0,0,0.02),0_24px_48px_-16px_rgba(10,10,10,0.12)]',
          success: 'border-success',
          error: 'border-error',
          warning: 'border-warning',
          info: 'border-line',
          actionButton:
            'bg-ink text-bg rounded-md px-3 py-1.5 font-sans text-small font-medium hover:bg-ink-2',
          cancelButton:
            'bg-transparent text-ink border border-line-2 rounded-md px-3 py-1.5 font-sans text-small hover:bg-bg-warm',
          closeButton:
            'bg-transparent border border-line-2 rounded-md text-ink hover:bg-bg-warm',
        },
      }}
    />
  );
}
